import React, { useEffect, useState } from 'react';
import {
	View,
	TouchableOpacity,
	StyleSheet,
	Platform,
	useWindowDimensions,
} from 'react-native';
import { ZenIcon, ZenText } from 'zen-core-elements';
import SellscreenModal from '../sellscreenModal';
import { Theme, ThemeConsumer } from '@rneui/themed';
import { CustomSearchBar } from '../customSearchBar';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { addTip } from '../../../redux/slices/newSellscreenSlice';
import { isValidAmount } from '../../../redux/utils/discountUtils';
import CommonService from '../../../shared/services/commonService';
import { webDialogWidth } from '../../../platform/design/dialogWeb';

const TipModal = ({ visible, onClose }: {
	visible: boolean;
	onClose: () => void;
}) => {
	let minTipValue = 9
	const { width, height } = useWindowDimensions()
	const dispatch = useAppDispatch()
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const { tip } = useAppSelector((state) => state?.NewSellscreenSlice)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const [value, setValue] = useState<null | string>(null);

	const presets = ['10', '50', '100', '200'];

	useEffect(() => {
		if (visible) {
			setValue(tip[locationId]?.toString() || null)
		}
	}, [visible])


	const onchangeText = (text: string | null) => {
		if (!text || isValidAmount(text)) {
			setValue(text)
		}
	}

	const handleSave = () => {
		dispatch(addTip({ tip: Number(value), locationId }))
		onClose()
	}
	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<SellscreenModal isModalOpen={visible} setModalOpen={onClose}>
						<View style={[{ minWidth: width > 900 ? width * 0.4 : width > 600 ? width * 0.60 : width * 0.80, maxHeight: width > 900 ? height * 0.60 : height * 0.40, }, webDialogWidth('md', width)]}>
							{/* Header */}
							<View style={styles.header}>
								<ZenText content={'Tip'} style={styles.headerTitle} />
								<TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
									<ZenIcon name='close' type='antdesign' color={theme?.lightDark} size={24} />
								</TouchableOpacity>
							</View>

							<View style={styles.content}>
								{/* Value Input */}
								<ZenText content='Tip Value' style={styles.label} />

								<View style={{ paddingBottom: 10 }}>
									<CustomSearchBar
										placeholder='Enter a value'
										search={value}
										setSearch={onchangeText}
										toggleOffset={() => { }}
										backgroundColor={theme?.iceWhite}
										leftIcon={false}
										border={true}
										height={50}
										keyboardType='number-pad'
										leftComponent={<ZenText content={currencySymbol} style={styles.currencyPrefix} />}
									/>
								</View>

								{/* Quick Presets */}
								<View style={styles.presetRow}>
									{presets.map((p) => (
										<TouchableOpacity
											key={p}
											style={styles.presetChip}
											onPress={() => setValue(p)}
										>
											<ZenText content={`+${CommonService?.currencyStandard(currencySymbol, p?.toString())}`} style={[styles.presetText]} />
										</TouchableOpacity>
									))}
								</View>
							</View>

							{/* Footer Actions */}
							<View style={styles.footer}>
								<TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
									<ZenText content='Cancel' style={styles.cancelBtnText} />
								</TouchableOpacity>
								<TouchableOpacity
									style={[styles.addBtn, (!value || Number(value) <= minTipValue) && styles.disabledBtn]}
									disabled={!value || (Number(value) < minTipValue)}
									onPress={handleSave}
								>
									<ZenText content={'Add Tip'} style={[styles.addBtnText, (!value || Number(value) <= minTipValue) && { color: theme?.disableTextColor }]} />
								</TouchableOpacity>
							</View>
						</View>
					</SellscreenModal>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		// paddingHorizontal: 24,
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: theme?.softSlate,
	},
	headerTitle: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.bold,
		color: theme?.lightDark,
	},
	content: {
		paddingVertical: 16
	},
	label: {
		fontSize: theme?.errorFontSize,
		fontFamily: theme?.medium,
		color: theme?.disabledGray500,
		marginBottom: 8,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	segmentContainer: {
		flexDirection: 'row',
		backgroundColor: theme.softSlate,
		borderRadius: 12,
		padding: 4,
		marginBottom: 24,
	},
	segment: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: 10,
		borderRadius: 8,
		gap: 8,
	},
	activeSegment: {
		backgroundColor: theme?.light,
		...Platform.select({
			ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 4 },
			android: { elevation: 2 },
		}),
	},
	segmentText: {
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.medium,
		color: theme?.disabledGray500,
	},
	activeSegmentText: {
		color: theme?.primary,
		fontFamily: theme?.bold,
	},
	inputContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: theme?.iceWhite,
		borderWidth: 1.5,
		borderColor: theme?.coolGray,
		borderRadius: 16,
		paddingHorizontal: 16,
		height: 50,
	},
	currencyPrefix: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.medium,
		color: theme?.grey,
		marginRight: 5,
	},
	input: {
		flex: 1,
		fontSize: theme?.smallerFontSize,
		fontFamily: theme?.medium,
		color: theme?.lightDark,
	},
	presetRow: {
		flexDirection: 'row',
		gap: 10,
		marginTop: 16,
	},
	presetChip: {
		backgroundColor: theme?.tealPrimary,
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 10,
		borderWidth: 1,
		borderColor: theme?.lightPrimary,
	},
	presetText: {
		color: theme?.primary,
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.medium,
	},
	footer: {
		flexDirection: 'row',
		paddingTop: 16,
		gap: 12,
		borderTopWidth: 1,
		borderTopColor: theme.softSlate,
	},
	cancelBtn: {
		flex: 1,
		paddingVertical: 14,
		alignItems: 'center',
		borderRadius: 12,
		backgroundColor: theme?.iceWhite,
	},
	cancelBtnText: {
		color: theme?.disabledGray500,
		fontSize: theme?.smallFontSize,
		fontFamily: theme?.medium,
	},
	addBtn: {
		flex: 2,
		backgroundColor: theme?.primary,
		paddingVertical: 14,
		alignItems: 'center',
		borderRadius: 12,
	},
	disabledBtn: {
		backgroundColor: theme?.disableGray,
	},
	addBtnText: {
		color: theme?.light,
		fontSize: theme?.smallFontSize,
		fontFamily: theme?.medium,
	},
});

export default TipModal;